import { memo, useCallback, useEffect, useState } from 'react';
import isEqual from 'react-fast-compare';

import revokeObjectURL from 'Lib/utils/revokeObjectURL';

import { SPREAD_METHOD_PAD } from '../constants';
import type { AngularGradientFill } from '../types';
import { createGetColor, drawAngularGradient, normalizeGradientStops } from '../utils';
import AngularGradientPolygon from './AngularGradientPolygon';

/**
 * Converts a canvas to a blob asynchronously
 *
 * @param canvas - canvas element
 * @return resolves with the blob
 */
function canvasToBlob(canvas: HTMLCanvasElement) {
  return new Promise<Blob>((resolve, reject) => {
    try {
      canvas.toBlob((blob) => {
        resolve(blob!);
      });
    } catch (e) {
      reject(e);
    }
  });
}

type Props = AngularGradientFill['props'] & {
  id: string;
  canvasEnabled: boolean;
  from?: string;
  to?: string;
};

const AngularGradient = (props: Props) => {
  const {
    angle = 0,
    canvasEnabled = true,
    canvasSize = 200,
    from = '',
    id,
    scale = 1,
    spreadMethod = SPREAD_METHOD_PAD,
    stops = [],
    to = '',
  } = props;

  const [patternUrl, setPatternUrl] = useState<string | undefined>();

  const drawPattern = useCallback(async () => {
    // Can't do canvas server-side
    if (!__CLIENT__) {
      return;
    }
    const gradientStops = normalizeGradientStops(from, to, stops);
    const getColor = createGetColor(gradientStops, scale, spreadMethod);
    const canvas = drawAngularGradient(canvasSize, getColor, angle);
    const blob = await canvasToBlob(canvas);

    if (patternUrl) {
      revokeObjectURL(patternUrl);
      setPatternUrl(URL.createObjectURL(blob));
    }
  }, [angle, canvasSize, from, patternUrl, scale, spreadMethod, stops, to]);

  useEffect(() => {
    if (canvasEnabled) {
      drawPattern();
    }
  }, [angle, drawPattern, stops, scale, spreadMethod, canvasSize, canvasEnabled]);

  if (!patternUrl || !canvasEnabled) {
    return <AngularGradientPolygon {...props} />;
  }

  return (
    <pattern
      id={id}
      patternContentUnits="objectBoundingBox"
      patternUnits="objectBoundingBox"
      width={1}
      height={1}
    >
      <image preserveAspectRatio="none" width={1} height={1} x={0} xlinkHref={patternUrl} y={0} />
    </pattern>
  );
};

export default memo(AngularGradient, isEqual);
